import React, {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useReducer,
    useState,
} from 'react';

import {
    CustomDossierOpenSearch,
    DossierOpenSearch,
    useFormaliteService,
} from '@europrocurement/l2d-domain';
import { type DataProviderProps, type DataState } from '@europrocurement/l2d-modules/providers';
import { type DossierJsonldDossierReadNoteGroupTimestampableBlameambleGroup } from '@europrocurement/l2d-domain/openApi/ApiFormalite';

type CurrentDossierProviderState = DataState & {
    data: DossierOpenSearch | undefined;
};
type CurrentDossierProviderProps = DataProviderProps & {
    idDossier: string;
};

const INIT_CURRENT_DOSSIER_CTX = 'init_current_dossier_ctx';
const SET_CURRENT_DOSSIER_CTX = 'set_current_dossier_ctx';
const SET_CURRENT_DOSSIER_NOTES_CTX = 'set_current_dossier_notes_ctx';
const SET_CURRENT_DOSSIER_SIBLINGS_CTX = 'set_current_dossier_siblings_ctx';
const REFRESH_CURRENT_DOSSIER_CTX = 'refresh_current_dossier_ctx';
const UPDATE_CURRENT_DOSSIER_CTX = 'update_current_dossier_ctx';

type Action =
    | { type: 'init_current_dossier_ctx'; payload: DossierOpenSearch }
    | { type: 'set_current_dossier_ctx'; payload: DossierOpenSearch }
    | {
          type: 'set_current_dossier_notes_ctx';
          payload: DossierJsonldDossierReadNoteGroupTimestampableBlameambleGroup[];
      }
    | { type: 'set_current_dossier_siblings_ctx'; payload: CustomDossierOpenSearch[] }
    | { type: 'refresh_current_dossier_ctx' }
    | {
          type: 'update_current_dossier_ctx';
          payload: {
              dossierId: number;
              data: DossierOpenSearch;
          };
      };

const CurrentDossierContext = createContext<CurrentDossierProviderState | null>(null);
const CurrentDossierDispatchContext = createContext<React.Dispatch<Action> | null>(null);

function CurrentDossierReducer(
    state: CurrentDossierProviderState,
    action: Action,
): CurrentDossierProviderState {
    switch (action.type) {
        case INIT_CURRENT_DOSSIER_CTX: {
            return {
                ...state,
                loaded: true,
                data: action.payload,
            };
        }

        case SET_CURRENT_DOSSIER_CTX: {
            return {
                ...state,
                data: action.payload,
            };
        }

        case SET_CURRENT_DOSSIER_NOTES_CTX: {
            return {
                ...state,
                data: { ...state.data, notes: action.payload } as DossierOpenSearch,
            };
        }

        case SET_CURRENT_DOSSIER_SIBLINGS_CTX: {
            return {
                ...state,
                data: { ...state.data, siblings: action.payload } as DossierOpenSearch,
            };
        }

        // Update currentDossier and siblings if exist in context
        case UPDATE_CURRENT_DOSSIER_CTX: {
            return {
                ...state,
                data: {
                    ...state.data,
                    siblings: (state.data?.siblings ?? []).map((dossier) =>
                        dossier.id === action.payload.dossierId
                            ? { ...dossier, ...action.payload.data }
                            : dossier,
                    ) as DossierOpenSearch[],
                    ...action.payload.data,
                },
            };
        }

        case REFRESH_CURRENT_DOSSIER_CTX: {
            return {
                ...state,
                loaded: false,
            };
        }
        default: {
            // @ts-expect-error property type never
            throw new Error(`Unknown action: ${action.type}`);
        }
    }
}

const CurrentDossierProvider: React.FunctionComponent<
    PropsWithChildren<CurrentDossierProviderProps>
> = function ({ autoload, children, idDossier }) {
    const [state, dispatch] = useReducer(CurrentDossierReducer, {
        loaded: false,
        data: undefined,
    } as CurrentDossierProviderState);

    const { dossierOpenSearchApi, collectionOpenSearchApi, dossierApi } = useFormaliteService();
    const [isFetching, setIsFetching] = useState<boolean>(false);

    // Fetch current dossier by props params and dispatch in context
    const fetchAndInitDossier = async (dossId: string) => {
        setIsFetching(true);

        try {
            const res = await dossierOpenSearchApi.apiOpensearchDossiersIdGet({ id: dossId });
            const { collectionId } = res.data;
            const [siblingsResult, notesResult] = await Promise.allSettled([
                collectionId
                    ? collectionOpenSearchApi.apiOpensearchCollectionsIdGet({
                          id: String(collectionId),
                      })
                    : Promise.resolve(null),
                dossierApi.apiDossiersIdGet({
                    id: dossId,
                }),
            ]);

            const siblings =
                siblingsResult.status === 'fulfilled' && siblingsResult.value
                    ? (siblingsResult.value.data.dossiers as unknown as CustomDossierOpenSearch[])
                    : [];

            const notes = notesResult.status === 'fulfilled' ? notesResult.value.data.notes : [];

            const buildDossierState = {
                ...res.data,
                siblings,
                notes,
            } as unknown as DossierOpenSearch;

            dispatch({
                type: INIT_CURRENT_DOSSIER_CTX,
                payload: buildDossierState,
            });
        } catch (error) {
            console.error(error);
        } finally {
            setIsFetching(false);
        }
    };

    // Fetch current dossier by props params
    useEffect(() => {
        if (!autoload || state.loaded === true || isFetching === true) {
            return;
        }
        fetchAndInitDossier(idDossier);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossierOpenSearchApi, autoload, state.loaded]);

    return (
        <CurrentDossierContext.Provider value={state}>
            <CurrentDossierDispatchContext.Provider value={dispatch}>
                {children}
            </CurrentDossierDispatchContext.Provider>
        </CurrentDossierContext.Provider>
    );
};

const useCurrentDossierCtx = () => {
    const state = useContext(CurrentDossierContext);

    const isDataLoaded = (): boolean => state?.loaded || false;

    const getCurrentDossier = (): DossierOpenSearch | undefined => state?.data;

    return {
        dossier: state?.data,
        siblings: state?.data?.siblings,
        isDataLoaded,
        getCurrentDossier,
    };
};

const useCurrentDossierCtxDispatch = () => useContext(CurrentDossierDispatchContext);

// Méthode get
export {
    CurrentDossierProvider,
    CurrentDossierContext,
    CurrentDossierDispatchContext,
    // hooks
    useCurrentDossierCtx,
    useCurrentDossierCtxDispatch,
    // actions dispatch
    INIT_CURRENT_DOSSIER_CTX,
    SET_CURRENT_DOSSIER_CTX,
    SET_CURRENT_DOSSIER_NOTES_CTX,
    REFRESH_CURRENT_DOSSIER_CTX,
    UPDATE_CURRENT_DOSSIER_CTX,
};
